import type {
  CollectionCreateInput,
  CollectionUpdateInput,
} from "@/modules/collections/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { collectionsApi } from "./client";

export const collectionKeys = {
  all: ["collections"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["collections", "list", q] as const,
  detail: (id: string) => ["collections", "detail", id] as const,
};

export function useCollectionsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: collectionKeys.list(q),
    queryFn: () => collectionsApi.list(q),
  });
}

export function useCollectionQuery(id: string) {
  return useQuery({
    queryKey: collectionKeys.detail(id),
    queryFn: () => collectionsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CollectionCreateInput) => collectionsApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collectionKeys.all });
      toast.success("ສ້າງ Collection ສໍາເລັດ");
    },
    onError: (error: any) => {
      if (error.message?.includes("already exists")) {
        toast.error("ຊື່ Slug ນີ້ມີແລ້ວ", { 
          description: "ກະລຸນາປ່ຽນຊື່ອັງກິດເພື່ອສ້າງ Slug ໃໝ່" 
        });
      } else {
        toast.error("ສ້າງ Collection ລົ້ມເຫຼວ", { 
          description: error.message || "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ" 
        });
      }
    },
  });
}

export function useUpdateCollection(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CollectionUpdateInput) =>
      collectionsApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collectionKeys.detail(id) });
      qc.invalidateQueries({ queryKey: collectionKeys.all });
      toast.success("ແກ້ໄຂ Collection ສໍາເລັດ");
    },
    onError: (error: any) => {
      if (error.message?.includes("already exists")) {
        toast.error("ຊື່ Slug ນີ້ມີແລ້ວ", { 
          description: "ກະລຸນາປ່ຽນຊື່ອັງກິດເພື່ອສ້າງ Slug ໃໝ່" 
        });
      } else {
        toast.error("ແກ້ໄຂ Collection ລົ້ມເຫຼວ", { 
          description: error.message || "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ" 
        });
      }
    },
  });
}

export function useDeleteCollection() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => collectionsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collectionKeys.all });
    },
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}
